import { supabase } from '../lib/supabase';

export interface Company {
  id: number;
  name: string;
  status?: string;
  country?: string;
  city?: string;
  postal_code?: string;
  vat_number?: string;
  eori_number?: string;
  coc_number?: number;
  article_23?: boolean;
  phone_number?: string;
  created_at?: string;
}

export interface UserCompany {
  id: number;
  user_id: string;
  company_id: number;
  created_at: string;
  company?: Company;
}

// Call the admin-users edge function with appropriate parameters
const callAdminUsersFunction = async (
  operation: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
  body?: Record<string, unknown>,
  queryParams?: Record<string, string>
) => {
  // Build the URL with query parameters if provided
  let url = `admin-users/${operation}`;
  if (queryParams) {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      params.set(key, value);
    });
    url += `?${params.toString()}`;
  }

  try {
    const { data, error } = await supabase.functions.invoke(url, {
      method,
      body,
    });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error(`Error in ${operation}:`, error);
    return { data: null, error };
  }
};

// Get all users (with pagination)
export const getUsers = async (
  page = 1,
  pageSize = 10,
  search = '',
  sortField = 'created_at',
  sortOrder = 'descend'
) => {
  return callAdminUsersFunction('list', 'GET', undefined, {
    page: String(page),
    pageSize: String(pageSize),
    search,
    sortField,
    sortOrder,
  });
};

/** Fetch all auth users for dropdowns (agent portal linking, etc.) */
export const getAllUsers = async (search = '') => {
  return callAdminUsersFunction('list', 'GET', undefined, {
    page: '1',
    pageSize: '10000',
    search,
    sortField: 'email',
    sortOrder: 'ascend',
  });
};

// Get companies for a specific user
export const getUserCompanies = async (userId: string) => {
  return callAdminUsersFunction('user-companies', 'GET', undefined, {
    userId,
  });
};

// Get users for a specific company
export const getCompanyUsers = async (companyId: number) => {
  return callAdminUsersFunction('company-users', 'POST', {
    companyId,
  });
};

// Get available companies for linking
export const getCompanies = async (search = '') => {
  return callAdminUsersFunction('list-companies', 'GET', undefined, {
    search,
  });
};

// Link a user to a company
export const linkUserToCompany = async (userId: string, companyId: number) => {
  return callAdminUsersFunction('link-company', 'POST', {
    userId,
    companyId,
  });
};

// Unlink a user from a company
export const unlinkUserFromCompany = async (userId: string, companyId: number) => {
  return callAdminUsersFunction('unlink-company', 'POST', {
    userId,
    companyId,
  });
};

// Check if a user is an admin
export const isUserAdmin = async () => {
  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !sessionData.session) return false;
    
    const { data: adminData, error: adminError } = await supabase
      .from('admin_users')
      .select('user_id')
      .eq('user_id', sessionData.session.user.id)
      .single();

    return !!adminData && !adminError;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Add a new admin user
export const addAdminUser = async (userId: string) => {
  return callAdminUsersFunction('add-admin', 'POST', { userId });
};

// Remove an admin user
export const removeAdminUser = async (userId: string) => {
  return callAdminUsersFunction('remove-admin', 'DELETE', { userId });
};

// Update user metadata (first name, last name)
export const updateUserMetadata = async (
  userId: string,
  first_name: string,
  last_name: string
) => {
  return callAdminUsersFunction('update', 'PATCH', {
    userId,
    first_name,
    last_name,
  });
};

// Delete a user
export const deleteUser = async (userId: string) => {
  return callAdminUsersFunction('delete', 'DELETE', { userId });
};

// Reset user password (admin function)
export const resetUserPassword = async (userId: string, password: string) => {
  return callAdminUsersFunction('reset-password', 'PATCH', {
    userId,
    password,
  });
};

// Verify user's email (admin function)
export const verifyUserEmail = async (userId: string) => {
  return callAdminUsersFunction('verify-email', 'POST', { userId });
};

// Generate password reset link
export const generatePasswordResetLink = async (
  userId: string,
  redirectTo?: string
) => {
  return callAdminUsersFunction('generate-reset-link', 'POST', {
    userId,
    redirectTo,
  });
};

// Ban or unban a user
export const toggleUserBan = async (userId: string, banned: boolean) => {
  return callAdminUsersFunction('toggle-ban-user', 'PATCH', {
    userId,
    banned,
  });
};

 